import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import {
  ITCProduct,
  ITcProductStandardDetailsInformation,
} from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { useApi } from "../../../../../../shared/hooks/useApi";
import useNotification from "../../../../../../shared/hooks/useNotification";

interface RawMaterialsInfoProps {
  standardData: ITcProductStandardDetailsInformation[];
  productData: ITCProduct;
  rawMaterialData: any;
  setStandardData: React.Dispatch<
    React.SetStateAction<ITcProductStandardDetailsInformation[]>
  >;
  rawMaterialInfoData: any;
  TE_ID?: string;
  setTotalCertifiedWeight: (weight: number) => void;
  disableOnEdit: boolean;
  setDisableOnEdit: (disable: boolean) => void;
}

const TcproductStandard: React.FC<RawMaterialsInfoProps> = ({
  standardData,
  setStandardData,
  productData,
  rawMaterialData,
  rawMaterialInfoData,
  TE_ID = "",
  setTotalCertifiedWeight,
  disableOnEdit,
  setDisableOnEdit,
}) => {
  const [data, setData] =
    useState<ITcProductStandardDetailsInformation[]>(standardData);
  const [tableData, setTableData] =
    useState<ITcProductStandardDetailsInformation[]>(standardData);
  const { notify } = useNotification();
  const { executeGet } = useApi();
  const [labelGrades, setLabelGrades] = useState([]);
  const [getAPITcStandard, setAPITcStandard] = useState<any>([]);
  const [filteredRawMaterials, setFilteredRawMaterials] = useState([]);
  const [standardFilteredRawMaterials, setStandardFilteredRawMaterials] =
    useState([]);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<
    any[]
  >([]);
  const {
    isRestrictPopUpSave,
    setRestrictPopUpSave,
    getLabelGrades,
    getTCStandardGOTS,
    getRawMtrlCodes,
  } = useTransactionCertificateStore();
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("certified-products");
  const buildStandardMapping = (certifiedRawMaterialCodes: any[]) => {
    const mapping: { [key: string]: string[] } = {};

    certifiedRawMaterialCodes.forEach((item) => {
      const standards = item.standard.split("/"); // Split by '/'

      standards.forEach((std: string) => {
        const trimmedStd = std.trim();
        if (!mapping[trimmedStd]) {
          mapping[trimmedStd] = [];
        }
        // Add the original standard value if not already present
        if (!mapping[trimmedStd].includes(item.standard)) {
          mapping[trimmedStd].push(item.standard);
        }
      });
    });

    return mapping;
  };

  useEffect(() => {
    const currentRow = data[editingRowIndex];
    const selectedLabelGrade = currentRow?.tcProductStandardLabelGrade || "";

    if (
      selectedLabelGrade &&
      filteredRawMaterials.length > 0 &&
      certifiedRawMaterialCodes.length > 0
    ) {
      const standardCode = selectedLabelGrade.substring(0, 3);
      const dynamicStandardMapping = buildStandardMapping(
        certifiedRawMaterialCodes
      );
      const matchingStandards = dynamicStandardMapping[standardCode] || [];

      const filtered = filteredRawMaterials.filter((material: any) => {
        const materialInfo = certifiedRawMaterialCodes.find(
          (item: any) => item.material_code === material.value
        );
        return (
          materialInfo && matchingStandards.includes(materialInfo.standard)
        );
      });

      setStandardFilteredRawMaterials(filtered);
    } else {
      setStandardFilteredRawMaterials(filteredRawMaterials);
    }
  }, [filteredRawMaterials, data, editingRowIndex, certifiedRawMaterialCodes]);

  const isCertifiedRM = (code: string) => {
    let standard =
      certifiedRawMaterialCodes.find((item: any) => item.material_code === code)
        ?.standard || "";
    return getAPITcStandard.some((item: any) => standard.includes(item));
  };

  const certifiedStdRawMaterialWeight = () => {
    const netShippingWeight = Number(productData.tcNetShippingWeight);
    const supplementaryWeight = Number(
      productData.tcProductSupplementaryWeight
    );
    const rawMaterialPercentage = rawMaterialInfoData.reduce(
      (acc: any, item: any) =>
        acc +
        Number(
          isCertifiedRM(item.tcProductRawMaterialCode)
            ? item.tcProductRawMaterialPercentage
            : "0"
        ),
      0
    );
    let certifiedWeight =
      ((Number(netShippingWeight) - Number(supplementaryWeight)) *
        rawMaterialPercentage) /
      100;
    certifiedWeight = Number(certifiedWeight.toFixed(2));
    setTotalCertifiedWeight(certifiedWeight);
    return certifiedWeight;
  };

  const weightForEachRawMaterial = () => {
    const netShippingWeight = Number(productData.tcNetShippingWeight);
    const supplementaryWeight = Number(
      productData.tcProductSupplementaryWeight
    );

    return rawMaterialInfoData.map((item: any, index: number) => {
      const weight = isCertifiedRM(item.tcProductRawMaterialCode);
      if (!weight) return null;

      const rawMaterialPercentage = Number(item.tcProductRawMaterialPercentage);
      const certifiedWeight =
        ((netShippingWeight - supplementaryWeight) * rawMaterialPercentage) /
        100;

      return (
        <Typography key={index} fontWeight="400">
          {item.tcProductRawMaterialCode} - {certifiedWeight.toFixed(2)} (kg)
        </Typography>
      );
    });
  };

  const validationSchema = Yup.object().shape({
    tcProductStandardLabelGrade: Yup.string().when([], {
      is: () => !getTCStandardGOTS,
      then: Yup.string().required("TC Standard is required"),
      otherwise: Yup.string().nullable(),
    }),
    tcProductRawMaterialCodeStandardLabel: Yup.string().required(
      "Raw Material Code is required"
    ),
    tcProductCertifiedWeight: Yup.number()
      .when([], {
        is: () => !getTCStandardGOTS,
        then: Yup.number().required("Weight is required"),
        otherwise: Yup.number().nullable(),
      })
      .test(
        "exact-certified-weight",
        `Combined weight must equal ${certifiedStdRawMaterialWeight().toFixed(
          4
        )}`,
        function (value) {
          if (editingRowIndex < 0) return true;
          const sum = data.reduce((acc, row, idx) => {
            const w =
              idx === editingRowIndex
                ? Number(value)
                : Number(row.tcProductCertifiedWeight);
            return acc + w;
          }, 0);

          const target = certifiedStdRawMaterialWeight();

          // if ((sum - target) > 0.001) {
          //   return this.createError({
          //     message: `Combined weight ${sum.toFixed(2)} must exactly equal ${target.toFixed(2)}`,
          //   });
          // }
          return true;
        }
      ),
  });

  const fetchStandardDetails = async () => {
    setRestrictPopUpSave(true);
    const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS, {
      tcId: TE_ID,
    });
    setRestrictPopUpSave(false);
    if (response?.data) {
      setAPITcStandard(response.data.tcStandard);
    }
  };

  const fetchCertifiedRawMaterials = async () => {
    try {
      setRestrictPopUpSave(true);
      const response = await executeGet(
        TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES
      );
      setRestrictPopUpSave(false);
      if (response?.data) {
        setCertifiedRawMaterialCodes(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch certified raw materials", error);
    }
  };

  const formik = useFormik<ITcProductStandardDetailsInformation>({
    initialValues: {
      tcProductStandardLabelOrder: "",
      tcProductStandardLabelGrade: "",
      tcProductCertifiedWeight: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    data.forEach((item: any, index) => {
      item.tcProductStandardLabelOrder = index + 1;
    });
    updateField("standardInfo", data);
  }, [data]);

  useEffect(() => {
    setLabelGrades(getLabelGrades);
    fetchStandardDetails();
    fetchCertifiedRawMaterials();
  }, []);

  useEffect(() => {
    const allRawMaterials = getRawMtrlCodes || [];

    const selectedCodes = rawMaterialData
      .map((item: any) => item.tcProductRawMaterialCode)
      .filter((code: string) => code);

    const filtered = allRawMaterials.filter((material: any) =>
      selectedCodes.includes(material.value)
    );

    setFilteredRawMaterials(filtered);
  }, [rawMaterialData, getRawMtrlCodes]);

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: ITcProductStandardDetailsInformation = {
      tcProductStandardLabelOrder: "",
      tcProductStandardLabelGrade: "",
      tcProductCertifiedWeight: "",
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     setData((prevData) =>
    //       prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row))
    //     );
    //   } else {
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    setData(tableData);
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
    setDisableOnEdit(false);
  };

  const handleSaveRow = () => {
    Object.values(formik.errors).forEach((elt: any) => {
      notify(elt, "error");
    });

    if (editingRowIndex === null || !formik.isValid) return;

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
      index === editingRowIndex ? updatedRow : row
    );
    setData(newData);
    setTableData(newData);
    setStandardData(data);
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    setDisableOnEdit(false);
    formik.resetForm();
  };

  const columns: EditableColumnDef<ITcProductStandardDetailsInformation>[] = [
    {
      accessorKey: "tcProductStandardLabelGrade",
      header: "Standard Label Grade",
      Header: (
        <span>
          {!getTCStandardGOTS ? <i style={{ color: "red" }}>*</i> : <></>}{" "}
          Standard Label Grade
        </span>
      ),
      minSize: 100,
      maxSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="tcProductStandardLabelGrade"
          value={value}
          options={labelGrades}
          enableSearch={true}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialCodeStandardLabel",
      header: "Raw Material",
      Header: (
        <span>
          {!getTCStandardGOTS ? <i style={{ color: "red" }}>*</i> : <></>} Raw
          Material
        </span>
      ),
      minSize: 100,
      maxSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="tcProductRawMaterialCodeStandardLabel"
          value={value}
          options={standardFilteredRawMaterials}
          enableSearch={true}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ),
    },
    {
      accessorKey: "tcProductCertifiedWeight",
      header: "Raw Material Certified Weight (kg)",
      Header: (
        <span>
          {!getTCStandardGOTS ? <i style={{ color: "red" }}>*</i> : <></>} Raw
          Material Certified Weight
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductCertifiedWeight"
          value={value}
          inputProps={{ max: 100 }}
          type="number"
          onChange={(e) => {
            const newValue = e.target.value;
            onChange(newValue);
          }}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
    setRestrictPopUpSave(true);
    setDisableOnEdit(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setStandardData(updatedData);
  };

  return (
    <div>
      <Typography marginTop={10} fontWeight={"400"}>
        Total Certified Product Weight: {certifiedStdRawMaterialWeight()} (kg)
      </Typography>

      <Box marginTop={2}>
        <Typography fontWeight="400">
          Certified Weight across Raw Materials:
        </Typography>
        <Box marginTop={1}>{weightForEachRawMaterial()}</Box>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          border: "2px solid rgba(128, 128, 128, .2)",
          my: 4,
        }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton
            disabled={!formik.isValid || isRestrictPopUpSave || disableOnEdit}
            onClick={handleAddNewRow}
            sx={{ mb: 2 }}
          >
            Add Standard Details
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default TcproductStandard;
